import { useCallback } from "react";
import useDisclosure, { DisclosureHost } from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import {
  PAYER_ACTION,
  PAYER_CONFIRMATION,
  PAYER_MESSAGES,
  PayerAction,
  PayerRow,
  PayerStatusPrompt,
} from "../payers-constant";
import { getPayerStatusCopy, toPayerFormValues } from "../payers-utils";
import {
  useArchivePayer,
  usePayerUsageLookup,
  useUpdatePayerStatus,
} from "./use-payers-api";

type ConfirmableAction = keyof typeof PAYER_CONFIRMATION;

interface PendingPayerAction {
  action: ConfirmableAction;
  payer: PayerRow;
}

export const usePayerActions = (drawer: DisclosureHost) => {
  const showSnackbar = useSnackbar();
  const confirmation = useDisclosure(false);
  const { mutateAsync: updateStatus } = useUpdatePayerStatus();
  const { mutateAsync: archivePayer } = useArchivePayer();
  const statusConfirmation = useDisclosure(false);
  const lookupPayerUsage = usePayerUsageLookup();

  const pending: PendingPayerAction | undefined = confirmation.data;

  const statusPrompt: PayerStatusPrompt | undefined = statusConfirmation.data;

  const onStatusRequest = useCallback(
    async (payer: PayerRow) => {
      if (!payer.active) {
        statusConfirmation.open({ payer });
        return;
      }
      try {
        const usage = await lookupPayerUsage(payer.payerId);
        statusConfirmation.open({ payer, usage });
      } catch (error) {
        showSnackbar(getErrorSnackbar(error));
      }
    },
    [lookupPayerUsage, statusConfirmation, showSnackbar]
  );

  const onStatusConfirm = useCallback(async () => {
    if (!statusPrompt) return;
    const { payer } = statusPrompt;
    try {
      await updateStatus({ payerId: payer.payerId, status: !payer.active });
      showSnackbar({
        type: "success",
        message: payer.active
          ? PAYER_MESSAGES.INACTIVE_SUCCESS
          : PAYER_MESSAGES.ACTIVATE_SUCCESS,
      });
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
    } finally {
      statusConfirmation.close();
    }
  }, [statusPrompt, updateStatus, showSnackbar, statusConfirmation]);

  const onAction = useCallback(
    (action: PayerAction, payer: PayerRow) => {
      if (action === PAYER_ACTION.EDIT) {
        drawer.open({ values: toPayerFormValues(payer) });
        return;
      }
      confirmation.open({ action, payer });
    },
    [drawer, confirmation]
  );

  const onConfirm = useCallback(async () => {
    if (!pending) return;
    const { action, payer } = pending;

    try {
      const response = await archivePayer({
        payerId: payer.payerId,
        status: action === PAYER_ACTION.ARCHIVE,
      });
      showSnackbar(getSuccessSnackbar(response));
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
    } finally {
      confirmation.close();
    }
  }, [pending, archivePayer, showSnackbar, confirmation]);

  return {
    confirmation,
    confirmationCopy: pending ? PAYER_CONFIRMATION[pending.action] : undefined,
    onAction,
    onConfirm,
    statusConfirmation,
    statusCopy: statusPrompt ? getPayerStatusCopy(statusPrompt) : undefined,
    onStatusRequest,
    onStatusConfirm,
  };
};
